// SEO JSON-LD schema helpers. Pass results to <SEOHead schemaJson={...} />.

const BASE_URL = "https://finansovnavigator.com";
const SITE_NAME = "Финансов Навигатор";
const LOGO_URL = `${BASE_URL}/og-image.png`;
const AUTHOR_NAME = "Алекс В.";
const AUTHOR_URL = `${BASE_URL}/za-nas`;

// Stable @id anchors. Every nested publisher/author reference points at
// these node identifiers instead of repeating the full entity, so the
// whole site describes exactly one Organization, one WebSite and one Person.
export const ORG_ID = `${BASE_URL}/#organization`;
export const WEBSITE_ID = `${BASE_URL}/#website`;
export const AUTHOR_ID = `${BASE_URL}/avtor/aleks-vasilev#person`;
export const orgRef = () => ({ "@id": ORG_ID });

/**
 * Full Organization node used wherever schema.org requires a typed, named
 * entity (Review.author, Article.author). An @id-only reference is invalid
 * there — Google reports "Missing field name in author".
 */
export const orgAuthor = () => ({
  "@type": "Organization",
  "@id": ORG_ID,
  name: SITE_NAME,
  url: `${BASE_URL}/`,
});

/** Full Person node for pages with a visible human byline. */
export const personAuthor = () => ({
  "@type": "Person",
  "@id": AUTHOR_ID,
  name: AUTHOR_NAME,
  url: AUTHOR_URL,
});

/** Default author object for Review/Article schemas. */
export const authorRef = () => orgAuthor();

/** Drops affiliate / invite / tracking URLs from a sameAs list. */
export const cleanSameAs = (urls: (string | undefined)[]): string[] =>
  urls.filter((u): u is string => {
    if (!u) return false;
    const lower = u.toLowerCase();
    if (/[?&](ref|referral|refcode|invite|aff|affid|aff_id|clickid|utm_[a-z]+|gclid|fbclid|tag|partner|promo|code)=/.test(lower)) return false;
    if (/(go2cloud|admitad|awin|tradedoubler|impact\.com|linksynergy|partners?\.|\/invite\/|\/referral|\/ref\/|\/r\/)/.test(lower)) return false;
    return true;
  });

export const organizationSchema = (): Record<string, unknown> => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": ORG_ID,
  name: SITE_NAME,
  url: BASE_URL,
  logo: LOGO_URL,
  description:
    "Независими ревюта и сравнения на инвестиционни платформи, крипто борси и банки за български инвеститори.",
  sameAs: [],
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer support",
    url: `${BASE_URL}/kontakt`,
  },
});

export const websiteSchema = (): Record<string, unknown> => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  name: SITE_NAME,
  url: BASE_URL,
  inLanguage: "bg-BG",
  publisher: orgRef(),
});

export interface BreadcrumbItem {
  name: string;
  path: string;
}

export const breadcrumbSchema = (
  items: BreadcrumbItem[]
): Record<string, unknown> => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, idx) => ({
    "@type": "ListItem",
    position: idx + 1,
    name: item.name,
    item: `${BASE_URL}${item.path}`,
  })),
});

export interface ReviewSchemaInput {
  name: string;
  url: string; // official URL of reviewed entity
  rating: number;
  reviewBody: string;
  datePublished: string; // ISO date
  path: string; // page path on our site
}

export const reviewSchema = ({
  name,
  url,
  rating,
  reviewBody,
  datePublished,
  path,
}: ReviewSchemaInput): Record<string, unknown> => ({
  "@context": "https://schema.org",
  "@type": "Review",
  itemReviewed: {
    "@type": "FinancialService",
    name,
    url,
  },
  author: authorRef(),
  reviewRating: {
    "@type": "Rating",
    ratingValue: String(rating),
    bestRating: "5",
    worstRating: "1",
  },
  publisher: orgRef(),
  datePublished,
  reviewBody,
  url: `${BASE_URL}${path}`,
});

export interface FAQItem {
  question: string;
  answer: string;
}

export const faqSchema = (faqs: FAQItem[]): Record<string, unknown> => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
});

export interface ArticleSchemaInput {
  headline: string;
  description: string;
  path: string;
  datePublished?: string;
  dateModified?: string;
  image?: string;
}

export const articleSchema = ({
  headline,
  description,
  path,
  datePublished = "2026-01-01",
  dateModified = new Date().toISOString().split("T")[0],
  image = LOGO_URL,
}: ArticleSchemaInput): Record<string, unknown> => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline,
  description,
  url: `${BASE_URL}${path}`,
  image,
  inLanguage: "bg-BG",
  datePublished,
  dateModified,
  author: authorRef(),
  publisher: orgRef(),
});

export { BASE_URL, SITE_NAME, AUTHOR_NAME, AUTHOR_URL, LOGO_URL };
